import { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { api } from '@/lib/api';
import { useGuilds } from '@/lib/hooks';
import { PLUGINS } from '@/lib/plugins';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { AdminBackLink } from './AdminPanel';

/**
 * Aktions-Log (Admin-Panel, admins only): every change/action made through the
 * dashboard, newest first. Written by the web-api actionLog plugin; settings
 * changes carry before → after, IDs come with the names they had back then.
 */

interface Change {
  path: string;
  before?: unknown;
  after?: unknown;
  added?: unknown[];
  removed?: unknown[];
}

interface Entry {
  id: string;
  at: string;
  actorId: string;
  actorName: string | null;
  guildId: string | null;
  kind: string;
  action: string;
  status: 'pending' | 'ok' | 'failed' | null;
  error: string | null;
  details: {
    changes?: Change[];
    body?: Record<string, unknown>;
    target?: Record<string, string>;
    names?: Record<string, string>;
  } | null;
}

interface Page {
  entries: Entry[];
  nextCursor: string | null;
  actors: Array<{ id: string; name: string }>;
}

const KINDS: Record<string, string> = {
  settings: 'Einstellungen',
  panel: 'Panels',
  message: 'Nachrichten',
  moderation: 'Moderation',
  access: 'Zugriff',
  admin: 'Admin',
  backup: 'Backups',
  music: 'Musik',
  login: 'Logins',
  other: 'Sonstiges',
};

/** Path segments → readable labels (unknown keys stay as they are). */
const SEGMENTS: Record<string, string> = {
  tickets: 'Tickets',
  tempChannels: 'Temp Channels',
  welcome: 'Welcome',
  leave: 'Leave',
  autoMod: 'AutoMod',
  modMail: 'ModMail',
  logging: 'Logging',
  leveling: 'Leveling',
  selfRoles: 'Self-Roles',
  serverStats: 'Server-Stats',
  notifications: 'Notifications',
  patchNotes: 'Patch Notes',
  rulesPanel: 'Regeln-Panel',
  supportRulesPanel: 'Support-Regeln-Panel',
  disabledPlugins: 'Schalter aus',
  actionLogDisabled: 'Aktions-Log aus',
  acceptButton: 'Accept-Button',
  startRoleId: 'Startrolle',
  roleId: 'Rolle',
  channelId: 'Channel',
  enabled: 'Aktiviert',
  message: 'Nachricht',
  content: 'Text',
  title: 'Titel',
  description: 'Beschreibung',
  label: 'Beschriftung',
  emoji: 'Emoji',
  color: 'Farbe',
  imageUrl: 'Bild',
  backgroundUrl: 'Hintergrundbild',
  personen: 'Person',
  vorlagen: 'Vorlage',
  vorlage: 'Vorlage',
  rechte: 'Rechte',
  permissions: 'Command-Perms',
  xp: 'XP',
  level: 'Level',
  admins: 'Admins',
  prefix: 'Prefix',
  language: 'Sprache',
};

const PLUGIN_LABEL = new Map<string, string>(PLUGINS.map((p) => [p.key, p.label]));

const BODY_KEYS: Record<string, string> = {
  userId: 'Mitglied',
  channelId: 'Channel',
  roleId: 'Rolle',
  reason: 'Grund',
  durationSec: 'Dauer (s)',
  content: 'Text',
  message: 'Nachricht',
  query: 'Suche',
  volume: 'Lautstärke',
  prize: 'Preis',
  discordId: 'Person',
  templateId: 'Vorlage',
  nickname: 'Nickname',
  backupId: 'Backup',
};

export function ActionLogPage() {
  const guilds = useGuilds(true);
  const [guildId, setGuildId] = useState('');
  const [actorId, setActorId] = useState('');
  const [kind, setKind] = useState('');
  const [days, setDays] = useState('30');

  const query = useInfiniteQuery({
    queryKey: ['action-log', guildId, actorId, kind, days],
    initialPageParam: '',
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams({ days });
      if (guildId) qs.set('guildId', guildId);
      if (actorId) qs.set('actorId', actorId);
      if (kind) qs.set('kind', kind);
      if (pageParam) qs.set('cursor', pageParam);
      return api<Page>(`/api/admin/action-log?${qs}`);
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const guildName = new Map((guilds.data?.guilds ?? []).map((g) => [g.id, g.name]));
  const entries = query.data?.pages.flatMap((p) => p.entries) ?? [];
  const actors = query.data?.pages[0]?.actors ?? [];
  const selectClass = 'h-10 w-full rounded-md border border-input bg-background px-3 text-sm';

  return (
    <div className="mx-auto max-w-4xl">
      <AdminBackLink />
      <h1 className="mb-1 text-2xl font-semibold">Aktions-Log</h1>
      <p className="mb-6 text-sm text-muted-foreground">
        Alles, was im Dashboard geändert oder ausgeführt wurde. Einträge bleiben 90 Tage.
      </p>

      <Card className="mb-4">
        <CardContent className="grid grid-cols-1 gap-3 pt-6 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <Label>Server</Label>
            <select className={selectClass} value={guildId} onChange={(e) => setGuildId(e.target.value)}>
              <option value="">Alle Server</option>
              {(guilds.data?.guilds ?? []).map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label>Person</Label>
            <select className={selectClass} value={actorId} onChange={(e) => setActorId(e.target.value)}>
              <option value="">Alle Personen</option>
              {actors.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label>Art</Label>
            <select className={selectClass} value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="">Alle Arten</option>
              {Object.entries(KINDS).map(([k, label]) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label>Zeitraum</Label>
            <select className={selectClass} value={days} onChange={(e) => setDays(e.target.value)}>
              <option value="1">Heute + gestern</option>
              <option value="7">7 Tage</option>
              <option value="30">30 Tage</option>
              <option value="90">90 Tage</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {query.isLoading && <p className="text-muted-foreground">Lade Aktions-Log…</p>}
      {query.error && <p className="text-destructive">Fehler: {(query.error as Error).message}</p>}
      {!query.isLoading && entries.length === 0 && (
        <p className="text-sm text-muted-foreground">Keine Einträge für diese Auswahl.</p>
      )}

      <Card>
        <ul className="divide-y">
          {entries.map((e) => (
            <EntryRow key={e.id} entry={e} guildName={e.guildId ? guildName.get(e.guildId) ?? e.guildId : null} />
          ))}
        </ul>
      </Card>

      {query.hasNextPage && (
        <div className="mt-4 text-center">
          <Button variant="outline" disabled={query.isFetchingNextPage} onClick={() => query.fetchNextPage()}>
            {query.isFetchingNextPage ? 'Lade…' : 'Ältere laden'}
          </Button>
        </div>
      )}
    </div>
  );
}

function EntryRow({ entry, guildName }: { entry: Entry; guildName: string | null }) {
  const [open, setOpen] = useState(false);
  const d = entry.details ?? {};
  const names = d.names ?? {};
  const hasDetails = !!(d.changes?.length || (d.body && Object.keys(d.body).length) || d.target);
  const at = new Date(entry.at);

  return (
    <li className="px-4 py-3 text-sm">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="w-[6.5rem] shrink-0 text-xs tabular-nums text-muted-foreground" title={at.toLocaleString('de-DE')}>
          {at.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}{' '}
          {at.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
        </span>
        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
          {KINDS[entry.kind] ?? entry.kind}
        </span>
        <span className="font-medium">{entry.actorName ?? entry.actorId}</span>
        <span className="min-w-0 flex-1">{entry.action}</span>
        <StatusBadge status={entry.status} at={at} error={entry.error} />
      </div>
      <div className="mt-0.5 flex flex-wrap items-center gap-2 pl-[6.5rem] text-xs text-muted-foreground max-md:pl-0">
        {guildName ? <span>{guildName}</span> : <span>global</span>}
        {hasDetails && (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="inline-flex items-center gap-1 hover:text-foreground"
          >
            Details <ChevronDown className={cn('h-3 w-3 transition-transform', open && 'rotate-180')} />
          </button>
        )}
      </div>
      {open && hasDetails && (
        <div className="mt-2 space-y-1 rounded-md border p-2 text-xs md:ml-[6.5rem]">
          {d.target &&
            Object.entries(d.target).map(([k, v]) => (
              <div key={`t-${k}`}>
                <span className="text-muted-foreground">{BODY_KEYS[k] ?? k}:</span> {show(v, names)}
              </div>
            ))}
          {d.changes?.map((c, i) => (
            <div key={i} className="break-words">
              <span className="text-muted-foreground">{pathLabel(c.path, names)}:</span>{' '}
              {c.added || c.removed ? (
                <>
                  {c.added?.length ? <span className="text-success">+ {c.added.map((x) => item(c.path, x, names)).join(', ')}</span> : null}
                  {c.added?.length && c.removed?.length ? ' · ' : null}
                  {c.removed?.length ? <span className="text-destructive">− {c.removed.map((x) => item(c.path, x, names)).join(', ')}</span> : null}
                </>
              ) : (
                <>
                  <span className="text-muted-foreground line-through decoration-muted-foreground/50">{show(c.before, names)}</span>
                  {' → '}
                  <span>{show(c.after, names)}</span>
                </>
              )}
            </div>
          ))}
          {d.body &&
            Object.entries(d.body).map(([k, v]) => (
              <div key={`b-${k}`} className="break-words">
                <span className="text-muted-foreground">{BODY_KEYS[k] ?? k}:</span> {show(v, names)}
              </div>
            ))}
        </div>
      )}
    </li>
  );
}

function StatusBadge({ status, at, error }: { status: Entry['status']; at: Date; error: string | null }) {
  if (!status) return null;
  if (status === 'ok') return <span className="text-xs text-success">ausgeführt ✓</span>;
  if (status === 'failed')
    return (
      <span className="text-xs text-destructive" title={error ?? undefined}>
        fehlgeschlagen{error ? `: ${error.slice(0, 80)}` : ''}
      </span>
    );
  // The bot didn't report back (not every action does) — just say it was sent.
  const stale = Date.now() - at.getTime() > 2 * 60_000;
  return <span className="text-xs text-muted-foreground">{stale ? 'gesendet' : 'läuft…'}</span>;
}

function pathLabel(path: string, names: Record<string, string>) {
  return path
    .split('.')
    .map((seg) => names[seg] ?? SEGMENTS[seg] ?? seg)
    .join(' › ');
}

/** List entries: plugin switches by their label, everything else like show(). */
function item(path: string, v: unknown, names: Record<string, string>): string {
  if (path === 'disabledPlugins' && typeof v === 'string') return PLUGIN_LABEL.get(v) ?? v;
  return show(v, names);
}

/** A stored value as text: IDs by name, booleans as an/aus, objects compact. */
function show(v: unknown, names: Record<string, string>): string {
  if (v === undefined || v === null || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'an' : 'aus';
  if (typeof v === 'string') return names[v] ?? v;
  if (typeof v === 'number') return String(v);
  if (Array.isArray(v)) return v.length ? v.map((x) => show(x, names)).join(', ') : '—';
  return JSON.stringify(v, (_k, x) => (typeof x === 'string' && names[x] ? names[x] : x));
}
